<template>
    <div class="create-guide">
        <div class="create-guide__info">
            <div class="create-guide__info-title">
                {{ info.create.title }}
            </div>
            <div class="create-guide__info-subtitle">
                {{ info.create.subtitle }}
            </div>
        </div>
        <div class="create-guide__action">
            <pendo-button
                :loading="loading"
                theme="app"
                type="secondary"
                class="ask-pendo-next-actions__button"
                size="small"
                @click="onCreateGuide">
                {{ $t('askPendo.nextActions.Create_guide') }}
            </pendo-button>
            <pendo-modal
                :visible="isModalVisible"
                :height="'250'"
                :title="$t('askPendo.nextActions.Select_app')"
                @close="closeModal">
                <template #body>
                    <pendo-multiselect
                        v-model="chosenApp"
                        :labels="{ top: $t('askPendo.nextActions.Select_app') }"
                        label-key="name"
                        value-key="id"
                        :allow-empty="false"
                        :max-menu-height="180"
                        :options="options">
                        <template #option="{ option }">
                            <pendo-icon-option :option="option" />
                        </template>
                        <template #selectedLabel="{ option }">
                            <pendo-icon-option :option="option" />
                        </template>
                    </pendo-multiselect>
                </template>
                <template #footer>
                    <pendo-button
                        theme="app"
                        type="secondary"
                        :label="$t('askPendo.nextActions.Cancel')"
                        @click="closeModal" />
                    <pendo-button
                        theme="app"
                        type="primary"
                        data-cy="cloneGuide"
                        @click="confirmCreateGuide">
                        {{ $t('askPendo.nextActions.Create_guide') }}
                    </pendo-button>
                </template>
            </pendo-modal>
        </div>
    </div>
</template>

<script>
import { PendoButton, PendoModal, PendoMultiselect, PendoIconOption } from '@pendo/components';
import { v4 as uuid } from 'uuid';
import get from 'lodash/get';
import { $can } from '@pendo/permissions';
import { mapGetters, mapActions } from 'vuex';
import { buildSegment } from '@/ask-pendo/next-actions/next-actions-utils.js';
import { compileSegmentPipeline } from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    components: { PendoButton, PendoModal, PendoMultiselect, PendoIconOption },
    props: {
        conversationId: {
            type: String,
            required: true,
        },
        customers: {
            type: Object,
            required: true,
        },
    },
    data () {
        const info = Object.freeze({
            create: {
                title: this.$t('askPendo.nextActions.Create_guide'),
                subtitle: this.$t('askPendo.nextActions.Create_guide_subtitle'),
            },
        });

        return {
            info,
            chosenApp: '',
            isModalVisible: false,
            loading: false,
        };
    },
    computed: {
        ...mapGetters({ apps: 'apps/list' }),
        ...mapGetters('askPendo', ['conversationById']),
        conversation () {
            return this.conversationById(this.conversationId);
        },
        options () {
            const canCloneToApps = this.apps
                .filter((app) => this.canCreateGuides(app.id))
                .map((app) => {
                    return { id: `${app.id}`, name: `${app.displayName}`, platform: `${app.platform}` };
                });

            return canCloneToApps;
        },
    },
    methods: {
        ...mapActions('segments', ['simpleSave', 'loadOne']),
        buildGuideOptions (productArea, savedSegment) {
            return {
                initialMessage: `Draft a light box guide inviting customers to take part in product discovery. The goal of the product discovery is to learn more about "${productArea}". Highlight the product discovery will be conducted by the product team, and their feedback will help to evolve the product. Make the tone of the guide inviting and highlight This should include a primary CTA to "schedule call" and a secondary CTA of "No thanks".`,
                segment: {
                    id: savedSegment.id,
                    name: savedSegment.name,
                },
            };
        },
        buildGuideRouteData (productArea, savedSegment, appId) {
            const options = this.buildGuideOptions(productArea, savedSegment);

            return this.$router.resolve({
                name: 'GuidesConversationalAiPage',
                params: {
                    appId,
                    template: 'announcement',
                    conversationId: uuid(),
                },
                query: {
                    options: encodeURIComponent(JSON.stringify(options)),
                },
            });
        },
        closeModal () {
            this.isModalVisible = false;
        },
        onCreateGuide () {
            if (this.apps.length > 1) {
                this.isModalVisible = true;
            } else {
                this.createGuide(this.apps[0].id);
            }
        },
        confirmCreateGuide () {
            this.isModalVisible = false;
            this.createGuide(this.chosenApp.id);
        },
        async createGuide (appId) {
            try {
                this.loading = true;

                const productArea = get(this.conversation, 'state_details.cf_productArea', '');
                const segmentId = get(this.conversation, 'state_details.cf_segmentId', '');
                let segment;
                if (!segmentId) {
                    const draftSegment = buildSegment(this.customers, productArea);
                    const pipelineProps = await compileSegmentPipeline(draftSegment);
                    draftSegment.pipeline = pipelineProps?.pipeline;
                    segment = await this.simpleSave({ segment: draftSegment });
                } else {
                    segment = await this.loadOne({ id: segmentId });
                }

                const routeData = this.buildGuideRouteData(productArea, segment, appId);
                window.open(routeData.href, '_blank');
            } finally {
                this.loading = false;
            }
        },
        canCreateGuides (appId) {
            return $can(appId)
                .entity('guide')
                .action('create')
                .source('ANY')
                .target('ANY');
        },
    },
};
</script>

<style scoped lang="less">
.create-guide {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__info-title {
        color: var(--palette-gray-100);
        font-weight: 600;
        font-size: 13px;
        line-height: 18px;
    }

    &__info-subtitle {
        color: var(--palette-gray-70);
        font-size: 12px;
        line-height: 18px;
    }
}
</style>
